import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { genresData, genreTimelineData } from "../../data/genresData";
import { genresZh } from "../../data/genresTranslations";
import "./SubgenreDetailPage.css";
import { getPhotographers } from "../../utils/apiService";

const TIMELINE_LINKED_SUBGENRES = new Set([
  "street-photography",
  "war-photography",
  "documentary-photography",
  "social-documentary",
  "conceptual-photography",
  "staged-tableau-photography",
  "surreal-photography",
  "postmodernism-in-photography",
  "i-novel-photography",
  "self-portraiture",
  "domestic-family-photography",
  "feminist-photography",
  "classic-landscape",
  "new-topographics",
  "urban-landscape",
  "fashion-photography",
  "product-photography",
  "advertising-photography",
  "film-based-experiments",
  "photomontage",
  "instant-polaroid",
  "ai-cgi-photography",
]);

// 清理姓名函数：去除重音符号、前导符号，转大写
function cleanName(name = "") {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/^[^A-Za-z0-9]+/, "")
    .trim()
    .toUpperCase();
}

function escapeRegExp(value = "") {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderTimelineEventWithPhotographerLinks({
  text,
  photographerDirectory,
  enabled,
  language,
}) {
  if (!enabled || !text || photographerDirectory.length === 0) return text;

  const linkedNames = new Map();

  photographerDirectory.forEach((photographer) => {
    const name = photographer.name?.trim();
    if (!name || !photographer.id || !text.includes(name)) return;

    linkedNames.set(name, photographer);

    const nameParts = name.split(/\s+/);
    const surname = nameParts[nameParts.length - 1];
    const surnameIsUnique =
      nameParts.length > 1 &&
      surname.length >= 5 &&
      photographerDirectory.filter((item) =>
        item.name?.trim().endsWith(` ${surname}`)
      ).length === 1;

    if (surnameIsUnique && text.includes(surname)) {
      linkedNames.set(surname, photographer);
    }
  });

  if (linkedNames.size === 0) return text;

  const labels = Array.from(linkedNames.keys()).sort(
    (first, second) => second.length - first.length
  );
  const matcher = new RegExp(`(${labels.map(escapeRegExp).join("|")})`, "g");

  return text.split(matcher).map((segment, index) => {
    const photographer = linkedNames.get(segment);
    if (!photographer) return segment;

    return (
      <Link
        key={`${photographer.id}-${segment}-${index}`}
        to={`/photographers/${photographer.id}`}
        className="timeline-photographer-link"
        aria-label={
          language === "CN"
            ? `查看 ${photographer.name} 的摄影师页面`
            : `View ${photographer.name}'s photographer page`
        }
      >
        {segment}
      </Link>
    );
  });
}

function mapNationalityToChinese(nationality = "") {
  const key = String(nationality).trim();
  const map = {
    "U.S.": "美国",
    US: "美国",
    USA: "美国",
    "United States": "美国",
    America: "美国",
    "United Kingdom": "英国",
    UK: "英国",
    Britain: "英国",
    England: "英国",
    Wales: "威尔士",
    Scotland: "苏格兰",
    Ireland: "爱尔兰",
    France: "法国",
    Germany: "德国",
    Netherlands: "荷兰",
    Hungary: "匈牙利",
    Italy: "意大利",
    Spain: "西班牙",
    Portugal: "葡萄牙",
    Belgium: "比利时",
    Switzerland: "瑞士",
    Austria: "奥地利",
    Russia: "俄罗斯",
    Ukraine: "乌克兰",
    Poland: "波兰",
    Sweden: "瑞典",
    Norway: "挪威",
    Denmark: "丹麦",
    Finland: "芬兰",
    Luxembourg: "卢森堡",
    Albania: "阿尔巴尼亚",
    Greece: "希腊",
    Turkey: "土耳其",
    Iran: "伊朗",
    Israel: "以色列",
    Japan: "日本",
    China: "中国",
    India: "印度",
    Korea: "韩国",
    "South Korea": "韩国",
    Mexico: "墨西哥",
    Brazil: "巴西",
    Argentina: "阿根廷",
    Peru: "秘鲁",
    Chile: "智利",
    Cuba: "古巴",
    Canada: "加拿大",
    Australia: "澳大利亚",
    "New Zealand": "新西兰",
    Egypt: "埃及",
    Mali: "马里",
    Senegal: "塞内加尔",
    Nigeria: "尼日利亚",
    "South Africa": "南非",
    "Isle of Man": "马恩岛",
    Baltimore: "巴尔的摩",
    York: "约克",
  };

  return map[key] || key;
}

const SubgenreDetailPage = ({ language = "EN" }) => {
  const { subgenreId } = useParams();

  // Find the subgenre data
  let currentSubgenre = null;

  for (const genre of genresData) {
    const subgenre = genre.subgenres.find((sub) => sub.id === subgenreId);
    if (subgenre) {
      currentSubgenre = subgenre;
      break;
    }
  }

  // Get timeline data for this subgenre (defined after early-return guard)
  // const timelineEvents will be set after ensuring currentSubgenre exists

  // Photographer profiles and genre assignments both come from checked-in
  // static snapshots, so this page has no runtime database dependency.
  const [photographers, setPhotographers] = useState([]);
  const [photographerDirectory, setPhotographerDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentSubgenre) return;

    const loadPhotographers = async () => {
      setLoading(true);
      try {
        // 从随网站部署的静态 JSON 获取摄影师数据
        const result = await getPhotographers();
        console.log(
          `✅ Photographers loaded from ${result.source} (${result.itemCount} items)`
        );

        const data = result.data;
        if (!Array.isArray(data) || data.length === 0) {
          setError(
            language === "CN"
              ? "未收到摄影师数据"
              : "No photographers data received"
          );
          setPhotographers([]);
          setPhotographerDirectory([]);
          return;
        }

        setPhotographerDirectory(
          data
            .map((photographer) => ({
              id: photographer.photographerid,
              name: photographer.name,
            }))
            .filter((photographer) => photographer.id && photographer.name)
        );

        const assignmentByPhotographerId = new Map(
          (currentSubgenre.photographerAssignments || []).map((assignment) => [
            Number(assignment.photographer_id),
            assignment,
          ])
        );

        const filtered = data.filter((photographer) =>
          assignmentByPhotographerId.has(Number(photographer.photographerid))
        );

        const processed = filtered
          .map((p) => {
            const assignment = assignmentByPhotographerId.get(
              Number(p.photographerid)
            );
            const years = p.birthyear
              ? `${p.birthyear}`
              : language === "CN"
              ? "未知"
              : "Unknown";
            const nationality =
              language === "CN"
                ? p.nation_zh || mapNationalityToChinese(p.nation || "") || "未知"
                : p.nation || "Unknown";

            return {
              id: p.photographerid,
              name: p.name,
              years,
              nationality,
              sortKey: cleanName(p.name),
              isPrimary: Boolean(assignment?.is_primary),
            };
          })
          .sort(
            (a, b) =>
              Number(b.isPrimary) - Number(a.isPrimary) ||
              a.sortKey.localeCompare(b.sortKey)
          );

        setPhotographers(processed);
        setError(null);
      } catch (err) {
        setError(
          language === "CN"
            ? `加载摄影师失败：${err.message}`
            : `Failed to load photographers: ${err.message}`
        );
        setPhotographers([]);
        setPhotographerDirectory([]);
      } finally {
        setLoading(false);
      }
    };

    loadPhotographers();
  }, [subgenreId, currentSubgenre, language]);

  // If subgenre was not found, render error AFTER hooks to satisfy Rules of Hooks
  if (!currentSubgenre) {
    return (
      <div
        className={`subgenre-detail-page ${
          language === "CN" ? "subgenre-cn" : ""
        }`}
      >
        <div className="error-message">
          <h1>Subgenre not found</h1>
          <Link to="/genres" className="back-link">
            ← Back to Genres
          </Link>
        </div>
      </div>
    );
  }

  const timelineEvents = genreTimelineData[currentSubgenre.timelineId] || [];

  // 子种类 Introduction 文本映射
  const subgenreIntroductions = {
    "documentary-photography":
      language === "CN"
        ? "纪实摄影旨在如实记录真实的事件、环境与人物。它常关注社会议题、历史时刻或文化现象，为观者提供诚实而有力量的影像叙事。"
        : "Documentary photography aims to chronicle real events, environments, and people. It often highlights social issues, historical moments, or cultural phenomena, providing viewers with an honest and impactful visual narrative.",
    "street-photography":
      language === "CN"
        ? "街头摄影在公共空间中捕捉日常生活的瞬间，强调自发的互动、城市环境与街头正在发生的独特故事，常常折射出一座城市的文化与活力。"
        : "Street photography captures candid moments of everyday life in public spaces. It focuses on spontaneous interactions, urban environments, and the unique stories unfolding in the streets, often reflecting the culture and energy of a city.",
    "war-photography":
      language === "CN"
        ? "战争摄影记录冲突的真实面貌及其对人与土地的影响。它既是历史档案，也以强烈的画面提醒我们战争的人类代价。"
        : "War photography documents the realities of conflict and its impact on people and places. It serves as a historical record and a powerful reminder of the human cost of war.",
    "social-documentary":
      language === "CN"
        ? "社会纪实摄影关注人们的生活与处境，常呈现社会问题、不公与结构性变迁，以激发公众的关注与共情。"
        : "Social documentary photography focuses on capturing the lives and conditions of people, often highlighting social issues, injustices, or cultural changes to inspire awareness and empathy.",
    "conceptual-photography":
      language === "CN"
        ? "观念摄影以影像表达抽象想法或概念，重心在视觉叙事、象征与创造性诠释，而非对现实的字面再现。"
        : "Conceptual photography uses imagery to express abstract ideas or concepts. The focus is on visual storytelling, symbolism, and creative interpretation rather than literal representation.",
    "staged-tableau-photography":
      language === "CN"
        ? "舞台/图像摄影通过精心布置的场景进行拍摄，常类似戏剧布景；摄影师对一切元素进行导演，以建构叙事或视觉隐喻。"
        : "Staged or tableau photography involves carefully arranged scenes, often resembling theatrical sets. The photographer directs every element to create a narrative or visual metaphor.",
    "surreal-photography":
      language === "CN"
        ? "超现实摄影融合现实与想象，以梦境般的视觉与出人意料的并置激发情感、挑战常规感知。"
        : "Surreal photography blends reality and imagination, using dreamlike visuals and unexpected juxtapositions to evoke emotion and challenge perceptions.",
    "postmodernism-in-photography":
      language === "CN"
        ? "摄影中的后现代主义质疑传统叙事，拥抱反讽、拼贴与观念游戏，常常模糊现实与再现之间的界线。"
        : "Postmodernism in photography questions traditional narratives and embraces irony, pastiche, and conceptual play, often blurring the boundaries between reality and representation.",
    "i-novel-photography":
      language === "CN"
        ? "私摄影关注高度个人化的叙事，常常模糊自传与虚构的界线，邀请观者进入摄影者的内在世界。"
        : "I-Novel photography explores deeply personal narratives, often blurring the line between autobiography and fiction, and inviting viewers into the photographer’s inner world.",
    "self-portraiture":
      language === "CN"
        ? "自画像是摄影者将镜头对准自己，通过富有创造且常带内省的影像探讨身份、情感与自我表达。"
        : "Self-portraiture involves the photographer turning the camera on themselves, exploring identity, emotion, and self-expression through creative and often introspective imagery.",
    "domestic-family-photography":
      language === "CN"
        ? "家庭/家居摄影记录家中日常、关系与亲密片刻，赞颂家庭纽带的温度、复杂性与真实。"
        : "Domestic or family photography documents everyday life, relationships, and intimate moments within the home. It celebrates the warmth, complexity, and authenticity of family bonds.",
    "feminist-photography":
      language === "CN"
        ? "女性主义摄影关注性别、身份与赋权等主题，以有力的视觉叙事挑战刻板印象，凸显女性的经验与视角。"
        : "Feminist photography explores themes of gender, identity, and empowerment. It challenges stereotypes and highlights women’s experiences and perspectives through powerful visual storytelling.",
    "classic-landscape":
      language === "CN"
        ? "经典风景摄影展现自然环境之美，强调辽阔视野、戏剧性光线，以及土地与天空（及有时的人类元素）之间的互动。"
        : "Classic landscape photography showcases the beauty of natural environments, emphasizing vast vistas, dramatic lighting, and the interplay between land, sky, and sometimes human elements.",
    "new-topographics":
      language === "CN"
        ? "“新地形”指以中性、客观的方法记录被人类改造的景观，常关注郊区开发、工业场地与环境面貌的变化。"
        : "New Topographics refers to a style that documents man-altered landscapes with a neutral, objective approach. It often focuses on suburban development, industrial sites, and the changing face of the environment.",
    "urban-landscape":
      language === "CN"
        ? "城市风景摄影捕捉城市的建筑、基础设施与氛围，强调人与其建成环境之间的互动。"
        : "Urban landscape photography captures the architecture, infrastructure, and atmosphere of cities. It highlights the interaction between people and their built environment.",
    "fashion-photography":
      language === "CN"
        ? "时尚摄影呈现服装、配饰与风格；多用于杂志或广告，融合创意统筹、造型与强烈视觉以传达潮流与审美。"
        : "Fashion photography highlights clothing, accessories, and style. Often produced for magazines or advertising, it combines creative direction, styling, and striking visuals to convey trends and aesthetics.",
    "product-photography":
      language === "CN"
        ? "产品摄影专注于商业呈现物品，强调清晰度、细节与吸引力，帮助品牌以最佳方式呈现产品。"
        : "Product photography is dedicated to showcasing items for commercial use. It emphasizes clarity, detail, and appeal, helping brands present their products in the best possible light.",
    "advertising-photography":
      language === "CN"
        ? "广告摄影旨在推广产品、服务或品牌，结合创意与技术，制作吸引并说服受众的图像。"
        : "Advertising photography is created to promote products, services, or brands. It combines creativity and technical skill to produce compelling images that attract and persuade consumers.",
    "film-based-experiments":
      language === "CN"
        ? "基于胶片的实验摄影探索双重曝光、泡片或替代工艺等创造性技术，拓展传统模拟摄影的边界。"
        : "Film-based experimental photography explores creative techniques such as double exposure, film soup, or alternative processes, pushing the boundaries of traditional analog photography.",
    photomontage:
      language === "CN"
        ? "照片拼贴将多张照片合成为一幅图像，营造超现实或观念性的构图，支持富于想象的叙事与视觉实验。"
        : "Photomontage combines multiple photographs into a single image, creating surreal or conceptual compositions. It allows for imaginative storytelling and visual experimentation.",
    "instant-polaroid":
      language === "CN"
        ? "即时/宝丽来摄影使用曝光后即可输出成片的相机，因其自发性、独特的色彩风格与可触及的独一无二成像而受青睐。"
        : "Instant photography uses cameras that produce prints immediately after exposure. It’s valued for its spontaneity, unique color palette, and tangible, one-of-a-kind results.",
    "ai-cgi-photography":
      language === "CN"
        ? "AI/CGI 摄影借助人工智能或计算机生成图像来创建或增强照片，为视觉叙事与艺术实验开辟新的可能。"
        : "AI-generated and CGI photography use artificial intelligence or computer-generated imagery to create or enhance photographs, opening new possibilities for visual storytelling and artistic experimentation.",
  };

  // 中文时间线：对齐英文原文逐条直译（仅为中文界面使用）
  const timelineZh = {
    "timeline-documentary-photography": [
      {
        year: 1839,
        event:
          "Louis Daguerre 的银版摄影法正式公布，标志着实用摄影的诞生。早期摄影师随即使用这一新媒介记录真实生活场景，最初的纪实影像由此出现。",
      },
      {
        year: 1890,
        event:
          "Jacob Riis 出版《How the Other Half Lives》，这本开创性的新闻摄影著作揭露了纽约廉租公寓的恶劣状况。该作品推动了社会改革，并确立了纪实摄影作为社会变革工具的地位。",
      },
      {
        year: 1935,
        event:
          "美国农场安全局（FSA）启动摄影项目，记录大萧条。Dorothea Lange、Walker Evans 等摄影师拍摄了多幅标志性影像，其中包括 Dorothea Lange 的《Migrant Mother》（1936）。这些作品塑造了公众对这场危机的认识。",
      },
      {
        year: 1947,
        event:
          "Robert Capa、Henri Cartier-Bresson 等人创立 Magnum Photos。这一合作社模式提升了纪实摄影的地位，使摄影师能够更独立、诚实地报道世界事件与人类故事。",
      },
      {
        year: 1955,
        event:
          "Edward Steichen 在 MoMA 策划的《The Family of Man》展览呈现了来自世界各地的 500 余幅影像。这一影响深远的展览颂扬人类共同经验，也使纪实摄影的人文主义方法受到全球关注。",
      },
      {
        year: 1967,
        event:
          "John Szarkowski 在 MoMA 策划《New Documents》展览，展出 Diane Arbus、Lee Friedlander 与 Garry Winogrand 的作品。该展预示了一种更具个人视角、更为粗粝的纪实风格，并进一步模糊了纪实与艺术之间的界限。",
      },
      {
        year: 1976,
        event:
          "彩色摄影开始在纪实领域获得认可。William Eggleston 于 1976 年在 MoMA 举办的展览成为重要里程碑，证明彩色影像同样能够以新的情感深度呈现现实。这也标志着黑白摄影长期主导“严肃”纪实传统后的重要转折。",
      },
      {
        year: 1999,
        event:
          "向数字摄影的转变革新了纪实实践。到 20 世纪 90 年代末，数码相机与互联网让纪实摄影师能够更自由地拍摄，并在全球范围传播影像，同时也引发了有关图像操控的新伦理问题。",
      },
      {
        year: 2005,
        event:
          "社交媒体的兴起显著扩大了纪实摄影的传播范围。摄影师能够即时向全球分享影像，“公民记者”也开始使用拍照手机记录事件，由此推动了实时纪实叙事的兴起。",
      },
      {
        year: 2015,
        event:
          "纪实摄影继续通过众包项目与多媒体形式演进。例如，Everyday 系列社交媒体项目与大规模协作借助 Instagram 记录非洲、亚洲等地的日常生活，将纪实传统延续至 21 世纪。",
      },
    ],
    "timeline-street-photography": [
      {
        year: 1877,
        event:
          "摄影师 John Thomson 与作家 Adolphe Smith 出版《Street Life in London》，这是最早以城市贫民街头肖像为主题的摄影书之一。这一早期社会纪实实践虽然包含现场摆拍，仍被视为街头摄影的先驱之作。",
      },
      {
        year: 1890,
        event:
          "Eugène Atget 开始系统拍摄巴黎街道。从 19 世纪 90 年代到 20 世纪 20 年代，他持续记录建筑、橱窗与街景。尽管生前并不知名，他后来因将巴黎街道视为值得严肃对待的摄影题材而被誉为街头摄影先驱。",
      },
      {
        year: 1925,
        event:
          "首款紧凑型 35mm 相机 Leica I 问世。其便携性与高品质镜头使摄影师能够低调地捕捉转瞬即逝的时刻；Leica I 在 1925 年的亮相标志着抓拍式 35mm 街头摄影的开端。",
      },
      {
        year: 1930,
        event:
          "20 世纪 30 年代初，街头摄影开始被视为独立类型，尤其是在巴黎。Henri Cartier-Bresson 与 Brassaï 等人手持相机穿行城市，追寻“决定性瞬间”；Cartier-Bresson 的抓拍与 Brassaï 的巴黎夜景共同奠定了现代街头影像的范式。",
      },
      {
        year: 1958,
        event:
          "Robert Frank 出版《The Americans》。这本在美国公路旅行中拍摄完成的经典摄影书最初颇具争议，但其原始而富有诗意的街头与日常影像深刻影响了街头摄影，呈现出观看战后生活的个人化、非主流视角。",
      },
      {
        year: 1965,
        event:
          "20 世纪 60 年代中期至 70 年代初被视为街头摄影的“黄金时代”。在纽约，Garry Winogrand、Lee Friedlander、Diane Arbus 等人以 35mm 相机捕捉未经摆布的瞬间，形成充满能量与粗粝感的快照美学；Joel Meyerowitz 等摄影师也在 70 年代积极探索彩色街头摄影。",
      },
      {
        year: 1967,
        event:
          "MoMA 的《New Documents》（1967）同样影响了街头摄影。展览将 Diane Arbus、Lee Friedlander 与 Garry Winogrand 作为拥有独特视角的艺术家呈现，巩固了街头照片可以成为个人表达的理念，进一步模糊了艺术与纪实的边界。",
      },
      {
        year: 1994,
        event:
          "Colin Westerbeck 与 Joel Meyerowitz 合著的《Bystander: A History of Street Photography》出版，反映出人们对这一类型重新产生兴趣。该书系统梳理了街头摄影的历史与美学，也激励新一代摄影师带着相机走上街头。",
      },
      {
        year: 2000,
        event:
          "Nick Turpin 等人创建 in-PUBLIC，这是最早专注街头摄影的线上社群之一。与此同时，街头摄影师开始通过互联网分享影像，预示着数码时代街头摄影的全球复兴。",
      },
      {
        year: 2010,
        event:
          "数码技术推动街头摄影在全球迅速发展。智能手机与 Instagram 等平台让数百万人能够在公共空间进行抓拍；与此同时，Vivian Maier 拍摄于 20 世纪中叶的大量街头照片在 2007–2011 年间陆续被发现，使她在身后成名，也激发了公众对街头摄影艺术性的兴趣。",
      },
    ],
    "timeline-war-photography": [
      {
        year: 1847,
        event:
          "美墨战争被认为是首个被摄影记录的冲突。1847 年，一位不知名摄影师在墨西哥萨尔蒂约为驻扎的美军拍摄银版照片。虽然这些图像是静态的肖像、营地与战后场景，且未被用于新闻报道，但它们标志着战争摄影的开端。",
      },
      {
        year: 1855,
        event:
          "早期战争摄影师 Roger Fenton 受英国政府委派记录克里米亚战争。1855 年，他拍摄了 300 余幅照片，包括摆拍的营地场景与风景；其中《The Valley of the Shadow of Death》成为早期大规模战争影像记录的代表作品，但这批照片也在一定程度上淡化了战争的血腥。",
      },
      {
        year: 1862,
        event:
          "美国内战期间，Mathew Brady 的团队，尤其是 Alexander Gardner 与 Timothy O’Sullivan，拍摄了大量战斗后的场景。Gardner 于 1862 年在安提塔姆拍摄的阵亡士兵照片震撼公众；Brady 的展览《The Dead of Antietam》让战争的残酷直接进入公共视野。",
      },
      {
        year: 1936,
        event:
          "摄影记者 Robert Capa 在西班牙内战期间拍摄《The Falling Soldier》，据称定格了一名共和国民兵中弹倒下的瞬间。该照片发表于杂志后成为历史上最著名、也最具争议的战争照片之一，象征着战争的悲剧。",
      },
      {
        year: 1945,
        event:
          "美联社摄影师 Joe Rosenthal 于 1945 年 2 月 23 日拍下美国海军陆战队员在硫磺岛升旗的场景。该影像迅速成为二战时期爱国主义的象征，并获得 1945 年普利策奖，被认为是最具辨识度的战争照片之一。",
      },
      {
        year: 1968,
        event:
          "美联社摄影师 Eddie Adams 于 1968 年 2 月 1 日在西贡街头拍下处决越共战俘的一瞬，这张照片通常被称为《Saigon Execution》。影像在全球发表后为 Adams 赢得普利策奖，也以其直观的残酷推动了反战舆论。",
      },
      {
        year: 1972,
        event:
          "Nick Ut 拍下 9 岁女孩 Kim Phúc 在凝固汽油弹袭击后赤身奔跑的画面（1972 年 6 月 8 日），这张照片后来被称为《Napalm Girl》。该图获得 1973 年普利策奖，其全球影响推动公众重新审视战争，并促使人们关注凝固汽油弹对平民造成的伤害。",
      },
      {
        year: 1991,
        event:
          "海湾战争带来了新的新闻限制：美军实施严格的媒体联合采访池制度与审查，限制战斗影像的公开。尽管仍出现标志性作品，例如 Ken Jarecke 拍摄的《Highway of Death》，许多照片仍因限制而直到后来才进入公众视野。",
      },
      {
        year: 2004,
        event:
          "2004 年 4 月，阿布格莱布监狱虐囚的数码照片首先由 CBS 曝光，引发国际公愤，并凸显了即便是非专业者拍摄的图像也能深刻塑造战争叙事。The New York Times 指出，这些照片以前所未有的方式把战争的残酷带进美国家庭。",
      },
      {
        year: 2022,
        event:
          "俄乌战争期间，普通公民和记者使用智能手机与无人机获取战场及暴行影像，例如布查的照片，并通过社交媒体即时传播。战争摄影在告知全球受众方面的作用由此持续演变。",
      },
    ],
    "timeline-social-documentary": [
      {
        year: 1877,
        event:
          "John Thomson 与 Adolphe Smith 出版《Street Life in London》，该书以照片与随笔呈现伦敦街头小贩与贫民的生活。作为最早的社会纪实摄影著作之一，它试图忠实呈现底层群体的生活，并激发公众关切。",
      },
      {
        year: 1890,
        event:
          "Jacob Riis 的《How the Other Half Lives》面世，书中大量闪光灯照片记录纽约廉租公寓居民的生存状况。这项开创性的社会纪实项目震撼读者并直接推动住房改革，摄影由此被确立为促进社会变革的重要力量。",
      },
      {
        year: 1908,
        event:
          "Lewis Hine 受美国全国童工委员会委托，开始拍摄工厂、矿井与农场中的童工（1908–1917）。这些有力的肖像提高了公众意识，并推动反童工立法，进一步巩固了具有社会责任感的纪实摄影所扮演的角色。",
      },
      {
        year: 1935,
        event:
          "美国农场安全局（FSA）于 1935–1944 年开展纪实计划，以支持新政。Walker Evans、Dorothea Lange、Gordon Parks 等摄影师建立了无与伦比的视觉档案，从佃农肖像到尘暴灾难，这些作品定义了此后数十年的社会纪实基调。",
      },
      {
        year: 1955,
        event:
          "纽约的《The Family of Man》展览虽然主题广泛，却包含大量来自世界各地、有关贫困、家庭与劳动的社会纪实影像。策展人 Edward Steichen 希望借此凸显“共同人性”；展览的广泛影响使纪实影像作为艺术与社会评论获得了全球性认可。",
      },
      {
        year: 1968,
        event:
          "Cornell Capa 提出“有担当的摄影”（concerned photographer）概念，并策划《The Concerned Photographer》展览与画册，呈现具有社会责任感的摄影记者。这项实践促使他在 1974 年创立 International Center of Photography（ICP），将社会纪实置于公共服务与教育的框架之中。",
      },
      {
        year: 1974,
        event:
          "International Center of Photography（ICP）在纽约成立，由 Cornell Capa 领导。其使命明确将“有担当的摄影”置于机构核心，支持关注社会议题的纪实项目，并培养新一代具有社会意识的摄影师。",
      },
      {
        year: 1985,
        event:
          "巴西摄影师 Sebastião Salgado 以宏大的社会纪实作品获得全球声誉，同时也伴随争议。他在 20 世纪 80 年代中期记录萨赫勒饥荒与非洲难民危机；《Sahel: Man in Distress》以及后来出版的《Workers》（1993）以强烈的黑白影像唤起人们对人道危机、劳动与剥削问题的关注。",
      },
      {
        year: 2010,
        event:
          "Brandon Stanton 创办《Humans of New York》。自 2010 年起，他通过街头肖像与受访者的简短自述讲述个人故事，以人性化方式呈现社会议题。该项目庞大的社交媒体受众展示了当代社会纪实的新形态。",
      },
      {
        year: 2012,
        event:
          "草根社会纪实项目在全球不断增加。例如，Everyday Africa Instagram 项目自 2012 年起以日常影像挑战刻板印象。此类众包计划与“阿拉伯之春”等事件中的公民新闻共同将社会纪实传统延展至数字时代。",
      },
    ],
    "timeline-conceptual-photography": [
      {
        year: 1840,
        event:
          "法国摄影师 Hippolyte Bayard 创作《Self Portrait as a Drowned Man》，在这张摆拍照片中假扮成一具尸体。尽管作品诞生于“观念摄影”一词出现的数十年前，它已是利用摄影呈现观念或虚构情境、而非单纯记录现实的早期例证。",
      },
      {
        year: 1968,
        event:
          "观念摄影作为 20 世纪 60 年代后期观念艺术运动的一部分兴起。艺术家开始重视图像背后的观念，而非形式之美。1967–1969 年间，Ed Ruscha 的艺术家书《Twentysix Gasoline Stations》等作品，以及 John Baldessari 的图文实践，都将照片视为承载观念的媒介，预示了“观念至上”的新取向。",
      },
      {
        year: 1976,
        event:
          "评论家 A. D. Coleman 在 1976 年发表文章《The Directorial Mode》，尝试界定这一新兴类型：摄影师如同电影导演一般为镜头安排场景。这回应了 70 年代艺术摄影中日益兴起的“通过构建图像表达观念”的倾向，也成为观念摄影的重要特征。",
      },
      {
        year: 1975,
        event:
          "女性艺术家采用观念策略挑战社会规范。Martha Rosler 在《Bringing the War Home》（1974）中将越战暴力影像与一尘不染的美国家居杂志照并置，形成对战争与媒介的有力批评；Hannah Wilke 的《S.O.S. Starification Object Series》（1975）则以带有口香糖“伤痕”的自画像探讨美与疼痛。这些实践显示，摄影可以用来发问，而不仅仅是再现。",
      },
      {
        year: 1977,
        event:
          "Cindy Sherman 开始创作《Untitled Film Stills》（1977–1980），以虚构电影女主角的造型拍摄黑白自画像。Sherman 的作品在观念摄影中具有奠基意义：每张图像都经过精心构建，唤起媒体对女性形象的典型想象；这种以自身、服装与表演承载观念的方法确立了该类型在艺术界的地位。",
      },
      {
        year: 1985,
        event:
          "舞台式/图像摄影在美术领域崛起，并与观念摄影相互交叠。Jeff Wall 以灯箱呈现的大幅彩色透明片《The Destroyed Room》（1978），通过精密布景与艺术史典故构建图像；Barbara Kruger 则在《Your Gaze Hits the Side of My Face》（1981）等作品中运用挪用与文字图像批评消费主义和男性凝视，再次强调观念优先于再现。",
      },
      {
        year: 1995,
        event:
          "观念摄影与其他媒介的边界逐渐模糊。20 世纪 90 年代，Andreas Gursky、Thomas Ruff、Thomas Demand 等与 Düsseldorf School 相关的艺术家创作出看似直接、实则经过高度构建或操控的图像，以反思全球化、拟像与影像过剩；与此同时，Photoshop（1990 年发布）让观念摄影师得以无缝合成此前难以实现的构想。",
      },
      {
        year: 2005,
        event:
          "进入 21 世纪，观念摄影在全球与数字层面持续扩展。艺术家将摄影与行为、影像和装置结合，讨论身份、记忆与监控等问题；例如 Vik Muniz 使用非常规材料搭建场景再进行拍摄，使“建构本身”成为主题。摄影愈发被视为执行观念的手段，最终呈现的照片只是整个创作过程的结果。",
      },
      {
        year: 2010,
        event:
          "美术馆与画廊全面接纳观念摄影。2010 年的《Manipulating Reality》以及 MoMA 的《New Photography》等展览经常呈现观念驱动的作品；Cindy Sherman 的大型回顾展也凸显其观念自画像对视觉文化的深远影响。社交媒体中，带有明确诉求的摆拍与超现实图像同样流行，证明 60 年代观念遗产的持久影响。",
      },
    ],
    "timeline-staged-tableau-photography": [
      {
        year: 1857,
        event:
          "Oscar Gustav Rejlander 在 Manchester Art Treasures Exhibition 展出《The Two Ways of Life》。这张由 32 张底片组成的大型合成照片，以寓言式布景呈现青年在德行与罪恶之间作出选择，被视为最早将有意摆拍的摄影作为艺术呈现的例子之一，也证明照片可以成为精心构造的叙事，而不只是对现实的即时记录。",
      },
      {
        year: 1858,
        event:
          "摄影师 Henry Peach Robinson 创作《Fading Away》，这张由五张底片合成的照片描绘家人守在垂危少女身边。作品于 1858 年展出时曾引发争议，一些观众认为这样的死亡场景不应被虚构；但它仍成为维多利亚时期图像摄影的里程碑，证明多张底片可以被无缝组合，以讲述完整的情感故事。",
      },
      {
        year: 1960,
        event:
          "观念艺术家 Yves Klein 创作《Leap into the Void》（1960）。他先在朋友托举的防护布上完成跳跃，再通过照片蒙太奇将防护布移除，从而制造出自己跃下屋顶的假象。这张以新闻照片形式发表的影像成为戏剧化舞台摄影服务于艺术神话建构的早期例证，也预示了后来图像摄影中的刻意构造。",
      },
      {
        year: 1976,
        event:
          "评论家 A. D. Coleman 于 1976 年在 Artforum 发表《The Directorial Mode》，以“导演式模式”界定这一类型。他指出，摄影师会主动为镜头安排和导演场景，这与未经设计的纪实方法不同；文章将 70 年代的实践与 19 世纪舞台摄影的谱系相连，并推动摆拍摄影获得更明确的艺术地位。",
      },
      {
        year: 1977,
        event:
          "Cindy Sherman 自 1977 年起创作《Untitled Film Stills》（至 1980），以虚构电影角色的造型在布置好的场景中进行自我拍摄。虽然这一系列常被纳入观念艺术讨论，但其通过假发、服装与道具营造的电影剧照感，同样成为小尺度图像摄影的典范。",
      },
      {
        year: 1978,
        event:
          "加拿大艺术家 Jeff Wall 推出《The Destroyed Room》（1978），以灯箱展示大幅彩色透明片，呈现一间被彻底翻乱的卧室布景。影院银幕般发光的呈现方式与精密布置，标志着 Wall 的 tableau vivant 实践起点；他随后通过《Mimic》《Picture for Women》等作品，将电影化规模与观念深度带入画廊。",
      },
      {
        year: 1991,
        event:
          "Gregory Crewdson 在 20 世纪 90 年代末开始创作《Twilight》系列，并于 2000 年在纽约展出。这些超现实的郊区夜景采用好莱坞式灯光与剧组规模，显示当时的图像摄影在制作上已可与电影片场比肩，并能在单帧画面中营造充满悬念的叙事。",
      },
      {
        year: 2005,
        event:
          "图像摄影在美术体系中进入主流。Philip-Lorca diCorcia 以隐藏灯光安排街景，Anna Gaskell 导演童话式情境，Gregory Crewdson 则以电影制作规模构建场景；《Theatre of Memory》（2005）等展览也集中呈现当代摆拍作品。到 2010 年代，数字工具让复杂合成成为可能，但许多艺术家仍偏好大型布景与真实演员，延续电影化传统。",
      },
      {
        year: 2018,
        event:
          "图像摄影的影响超越艺术圈，进入高级肖像、时尚乃至广告领域；与此同时，Alex Prager 等画廊艺术家延续这一方法，创作仿若电影剧照的鲜明群像。MoMA 的《Fabricated》（2018）等展览集中呈现当代摆拍作品，表明自 19 世纪发展而来的舞台/图像传统已经成为摄影艺术的重要表达方式。",
      },
    ],
    "timeline-surreal-photography": [
      {
        year: 1924,
        event:
          "超现实主义艺术运动随着 André Breton 的《Manifesto of Surrealism》（1924，巴黎）诞生，摄影很快成为其重要工具。20 世纪 20 至 30 年代，Man Ray、Dora Maar 等摄影师通过多重曝光、蒙太奇与陌生化并置创造梦境般的图像。例如，Man Ray 在《Le Violon d’Ingres》（1924）中为裸体女性的背部添加小提琴的 f 孔。这一时期确立了超现实摄影对潜意识与非理性的持续探索。",
      },
      {
        year: 1922,
        event:
          "Man Ray 发明 rayograph（photogram）技法：将物体直接置于感光纸上曝光，无需相机即可成像，《Untitled Rayograph》（1922）是其中的代表。这些无相机抽象影像形成幽灵般的梦境效果，并受到超现实主义诗人的推崇；他的《Glass Tears》（1932）等作品也为摄影注入了实验性与非理性精神。",
      },
      {
        year: 1934,
        event:
          "Hans Bellmer 自费出版《Die Puppe (The Doll)》，呈现他制作的一具等身女性人偶。该系列于 1934 年在超现实主义期刊《Minotaure》亮相；人偶四肢被拆解与重新组合的影像，体现了超现实主义对人体模型与潜意识欲望的迷恋，也为 20 世纪 30 年代中期的超现实摄影增添了令人不安的恋物色彩。",
      },
      {
        year: 1936,
        event:
          "在 1936 年的 London International Surrealist Exhibition 上，Dora Maar 展出《Portrait of Ubu》：一张被塑造成神秘生物的犰狳胎儿特写。这幅常被称为《Père Ubu》的离奇影像，体现了超现实主义从真实物体中发现诡异感的偏好；它也表明，即使不使用蒙太奇，直接摄影仍可通过题材与取景产生超现实效果。",
      },
      {
        year: 1947,
        event:
          "巴黎的 The International Surrealist Exhibition（1947）汇集了大量摄影作品与装置，标志着古典超现实主义发展至一个高点。二战后，传统超现实主义逐渐式微，但其摄影影响仍在延续。20 世纪 40 年代末至 50 年代，Clarence John Laughlin 与 Angus McBean 等摄影师继续创作哥特式、超现实影像，在不同时代之间形成连接。",
      },
      {
        year: 1967,
        event:
          "美国暗房艺术家 Jerry Uelsmann 于 1967 年在 MoMA 举办首次个展。他使用多台放大机，将风景、人体与奇幻元素无缝组合为黑白影像，在 Photoshop 出现前数十年便创造出如树木从手掌生长般的梦境场景。其 20 世纪 60 至 70 年代的摄影蒙太奇广受赞誉，也启发了新一代以模拟暗房手段创作想象性影像。",
      },
      {
        year: 1990,
        event:
          "Adobe发布Photoshop 1.0（1990），开启数字图像处理时代。90年代中期，艺术家与爱好者皆可在电脑上轻松制作超现实合成。大都会博物馆2012年展《Faking It: Manipulated Photography before Photoshop》回望“数码之前”的操控传统；90年代也迎来数码超现实摄影热潮——如英国设计师Storm Thorgerson的超现实唱片封面摄影、美国摄影师Maggie Taylor用扫描素材构建维多利亚风格的超现实场景。",
      },
      {
        year: 2013,
        event:
          "社交媒体让超现实摄影面向大众。瑞典摄影师Erik Johansson的脑洞合成（如将风景像床单般折起）在网络上病毒式传播，显示超现实影像的强大吸引力。同时，“被P过”（Photoshopped）成为常用语，关于创意超现实与误导性修图之间界线的公共讨论兴起。",
      },
      {
        year: 2021,
        event:
          "超现实摄影依然流行且富有影响力。Sandy Skoglund 以真人与道具搭建超现实场景，Instagram 等平台上的新锐数字艺术家也不断拓展这一类型；其美学同时渗透至广告与时尚。2021 年，大都会艺术博物馆的《Surrealism Beyond Borders》追溯超现实主义的全球遗产，其中包含多件摄影作品，印证近百年来摄影师仍持续从梦境与潜意识中汲取灵感。",
      },
    ],
    "timeline-postmodernism-in-photography": [
      {
        year: 1964,
        event:
          "Andy Warhol 展出取自宣传照片的 Marilyn Monroe 等流行偶像丝网版画，以大众媒介图像进入美术领域，预示了后现代艺术中的“挪用”策略。他通过重新使用与混合日常商业影像，将平凡的摄影图像提升为艺术，也提前提出了此后摄影将反复追问的原创性与作者身份问题。",
      },
      {
        year: 1977,
        event:
          "《The Pictures Exhibition》于 1977 年 9 月 24 日在纽约 Artists Space 开幕，由 Douglas Crimp 策划，参展者包括 Troy Brauntuch、Jack Goldstein、Sherrie Levine、Robert Longo 与 Philip Smith。该展后来被视为 The Pictures Generation 的起点。Crimp 指出，这些艺术家挪用电视、杂志、电影与广告中的影像，在图像内部制造新的现实。摄影由此成为可以被引用、摆拍与重新置入语境的材料，而不再只是现实的窗口；Crimp 于 1979 年发表的《Pictures》进一步巩固了这一观点。",
      },
      {
        year: 1977,
        event:
          "Cindy Sherman 开始创作《Untitled Film Stills》（约 70 幅，1977–1980），在虚构场景中扮演不同女性角色，模仿 20 世纪 50 至 60 年代 B 级电影与黑色电影的视觉质感。该系列以虚构取代所谓真实，并以反讽审视好莱坞的女性刻板形象。通过化装、表演与自我拍摄，Sherman 揭示了身份的建构性与摄影的戏剧性，对后现代艺术产生了深远影响。",
      },
      {
        year: 1979,
        event:
          "Sherrie Levine 以再摄影挑战艺术正统。1979 至 1981 年间，她重新拍摄 Walker Evans 在 20 世纪 30 年代创作的著名照片，形成《After Walker Evans》，并于 1981 年在纽约 Metro Pictures 展出。该系列既被誉为后现代主义的里程碑，也因直接挪用而受到批评。通过几乎不作改动地重拍既有照片，Levine 将原创性与作者资格的问题推到台前：图像的观念与语境，是否比最初的制作者更加重要？",
      },
      {
        year: 1987,
        event:
          "Richard Prince 以《Untitled (Cowboy)》系列引发争议：他直接重拍 Marlboro 广告中的牛仔图像，其中一幅于 1989 年登上《Artforum》封面。Prince 将产品与文字移除，只保留标志性形象，再以大型艺术照片呈现，由此模糊广告与艺术的边界，并评论美国神话与拟像。该系列后来在拍卖中达到高价，也显示“艺术家之手”的观念已经改变。2014 年，他在《New Portraits》中挪用 Instagram 截图而再次引发法律与伦理争论，其源头可追溯至这批 20 世纪 80 年代作品。",
      },
      {
        year: 1989,
        event:
          "80年代末，后现代摄影被体制全面吸纳：惠特尼美术馆《Image World: Art and Media Culture》（1989）展示运用大众媒介图像的艺术家；同年，洛杉矶MOCA《A Forest of Signs: Art in the Crisis of Representation》探讨“再现危机”。高校课程亦普及批评理论与影像研究。到1990年前后，挪用、摆拍、图文拼贴与身份表演等曾经的激进手法已走向中心。",
      },
      {
        year: 1991,
        event:
          "Smithsonian American Art Museum 举办《The Photography of Invention: American Pictures of the 1980s》。展览聚焦“发明而非发现”图像的摄影师，包括以文字覆盖照片的 Barbara Kruger、用玩偶安排场景的 Laurie Simmons，以及以玩具人物模拟历史场面的 David Levinthal。该展强化了对 20 世纪 80 年代美国后现代摄影的认识：摄影师既是导演，也是观念的作者；展名本身即强调媒介从“捕捉”转向“构造”。",
      },
      {
        year: 2001,
        event:
          "大都会艺术博物馆的《The Pictures Generation, 1974–1984》回溯 The Pictures Generation，梳理 Cindy Sherman、Sherrie Levine、Richard Prince、Barbara Kruger 等人如何形成“关于图像的图像”这一共同取向。至此，这批艺术家已被纳入艺术史正典，其影响遍及时尚、广告与更广泛的视觉文化；正如评论家 Peter Schjeldahl 所讥讽，挪用如今已经成为主流。",
      },
      {
        year: 2014,
        event:
          "社交媒体与自拍文化的爆发，可以被视为后现代摄影观念的大规模扩散。2013 年，“selfie”被 Oxford Dictionaries 评为年度词；到 2014 年，Instagram 与 Tumblr 上的用户通过文字、滤镜与再混合不断重塑图像，在日常层面延续了 Cindy Sherman、Barbara Kruger 等人的实践。高雅艺术与大众视觉文化的边界进一步消解；借用 Douglas Crimp 的说法，我们都成为了图像制造者。",
      },
    ],
    "timeline-i-novel-photography": [
      {
        year: 1960,
        event:
          "战后日本，一些摄影师开始采用近似“私小说”（私小説）的自白式第一人称方法，将镜头转向自己的生活。Masahisa Fukase 是其中的先行者；20 世纪 60 年代，他持续拍摄妻子 Yoko，系列《Yoko》呈现她的欢笑、裸体、日常与亲密时刻。这些高度个人化的影像打破了摄影必须保持超然客观的观念，体现了后来被称为“私写真”（I-photography）的实践。摄影评论家 Shōji Yamagishi 曾指出，私小说与摄影在第一人称叙事和亲密经验的表达上彼此接近。",
      },
      {
        year: 1971,
        event:
          "Nobuyoshi Araki 自费出版《Sentimental Journey》（1971），以一连串坦率而富有叙事性的影像记录他与妻子 Yoko 的蜜月，包括她在榻榻米上入睡、二人安静相处与身体亲密的时刻。该书的个人语调、日常美学与摄影日记形式，明显有别于正式的影棚摄影。它后来被视为 20 世纪日本最重要的摄影书之一，也成为“私写真”（shi-shashin／I-photography）的代表性作品。",
      },
      {
        year: 1974,
        event:
          "MoMA 举办由 John Szarkowski 策划的《New Japanese Photography》（1974），向国际观众介绍日本前卫摄影。除风景与街头影像外，西方观众也接触到强调个人叙事的作品，例如 Masahisa Fukase 与 Shōmei Tōmatsu 的摄影中便包含鲜明的主观与私语元素。Nobuyoshi Araki 虽未入选，但展览图录与相关评论已注意到日本摄影对第一人称表达的关注；MoMA 的平台也使这种“私摄影”倾向在海外获得重要认可。",
      },
      {
        year: 1979,
        event:
          "Shōji Yamagishi 在纽约 International Center of Photography（ICP）策划《Japan: A Self-Portrait》（1979），进一步将日本当代摄影框定为一种“国家的自画像”。他把 Nobuyoshi Araki 纳入展览，使其关于伴侣、日常与私人生活的影像首次在西方重要机构中展出。许多参展者，包括 Nobuyoshi Araki、Daidō Moriyama 与 Eikoh Hosoe，都从自身经验出发。至 20 世纪 70 年代末，私小说式的日本摄影已被国际摄影界视为一种独特而重要的潮流。",
      },
      {
        year: 1980,
        event:
          "Nobuyoshi Araki 出版《Pseudo-Reportage》（1980），进一步试探私人真实与虚构叙事的边界。该系列以近似新闻照片的黑白影像呈现街头打斗、情人旅馆与事故等场景，却配上刻意误导的文字说明。通过并置日记式、准纪实的影像与虚假叙述，他质疑摄影的真实性，也表明即使记录现实，所呈现的仍是摄影师个人的现实。此时，“私写真”（shi-shashin）开始进入日本批评语境；《Sentimental Journey》等作品中，Araki 既是摄影书作者，也是第一人称叙事的主角。",
      },
      {
        year: 1986,
        event:
          "Nobuyoshi Araki 于 1986 年出版摄影书《Shishōsetsu (I-Novel)》，直接连接文学私小说与其影像实践。书中的私人照片与文本把他的生活呈现为一部以摄影书写的开放式小说；从猫 Chiro 的日常，到与已故妻子 Yoko 有关的亲密影像，都成为同一部长篇自白叙事的一部分。20 世纪 80 年代，Kazuo Kitai、后来的 Takashi Homma 等摄影师也沿着关注家庭、故乡与自身生活的方向展开实践，使“以自己的生活为主角”逐渐成为重要路径。",
      },
      {
        year: 1995,
        event:
          "“Hiromix 现象”标志着新一代对私写真的继承与变化。17 岁的 Hiromi “Hiromix” Toshikawa 凭借 36 页小刊《Seventeen Girl Days》赢得 Canon《New Cosmos of Photography》大奖（1995 年 3 月），评审之一正是 Nobuyoshi Araki。刊物以模糊的自画像、内衣照、吃了一半的早餐和与朋友相处的日常构成一部青春记忆日记。她的胜出推动“女孩子摄影家”（おんなのこ写真家）获得认可，也鼓励年轻女性把镜头转向自己与身边世界。1996 年，Hiromix 出版首本摄影集《Girls Blue》，以松弛、低保真的快照美学将私写真带入下一代。",
      },
      {
        year: 1996,
        event:
          "批评家 Kotaro Iizawa 于 1996 年提出“おんなのこ写真家”（女孩子摄影家），概括 20 世纪 90 年代中后期日本年轻女性以摄影书写自身生活的浪潮。Yurie Nagashima 以全家裸体肖像讨论隐私与家庭，Mika Ninagawa 以鲜艳色彩记录个人瞬间，后来的 Momo Okabe 则持续拍摄酷儿关系与自我旅程。至 90 年代末，私写真由边缘进入主流，杂志与出版社积极推介这些新声音；Hiromix 也成为这一潮流的代表人物，并于 2000 年获得 Kimura Ihei Photography Award。",
      },
      {
        year: 2010,
        event:
          "进入 2010 年代，私小说式摄影在社交媒体中继续演化并走向全球，视觉日记式的日常分享不断模糊艺术与生活。Momo Okabe 出版《Bible》（2014）与《Ilmatar》（2015），以自身关系与性别转变经历为轴，直面亲密与疼痛，并明确表示其摄影建立在日本私小说与真实经验之上。与此同时，Rinko Kawauchi 在《UtATANE》等作品中、Lieko Shiga 在更具隐喻性的实践中，也把个人视角带入艺术摄影。日本以外，许多独立摄影书同样以家人、恋人与日常生活为主题，显示自白式影像已成为全球性的表达方式；Nobuyoshi Araki 也仍持续拍摄与出版。",
      },
    ],
    "timeline-self-portraiture": [
      {
        year: 1839,
        event:
          "美国化学家与摄影先驱 Robert Cornelius 使用达盖尔银版法拍下公认最早的摄影自画像之一（可视为早期“自拍”），需要静止数分钟。",
      },
      {
        year: 1840,
        event:
          "法国摄影师 Hippolyte Bayard 创作《Self Portrait as a Drowned Man》，以表演性的自我呈现表达自己在摄影发明史中遭到边缘化后的不平与挫败。",
      },
      {
        year: 1860,
        event:
          "随着肖像摄影普及，影楼摄影师偶尔会把自己纳入画面（手持相机或站在被摄者之间），以含蓄方式申明其职业与创作者身份。",
      },
      {
        year: 1966,
        event:
          "宇航员 Buzz Aldrin 在 Gemini 12 任务期间拍下被认为是历史上第一张太空自拍（1966），将自画像拓展至地球之外。",
      },
      {
        year: 1975,
        event:
          "艺术家 Francesca Woodman 以在破旧空间中的亲密自画像著称，将自传性与性别的自省带入艺术摄影。",
      },
      {
        year: 2013,
        event:
          "“Selfie（自拍）”成为流行语，牛津词典将其评为年度词。社交媒体使快速、前置镜头的自画像成为一种普通且重要的文化表达。",
      },
      {
        year: 2014,
        event:
          "Ellen DeGeneres 在奥斯卡颁奖礼拍摄的群星自拍，一度成为当时转发次数最多的照片，显示自拍作为公共连接与文化事件的传播力量。",
      },
      {
        year: 2020,
        event:
          "自画像完全融入全球视觉文化。智能手机、滤镜与社交媒体下，从时尚自拍到私人告白的日常自我呈现，已成为自我表达与数字身份的主流形式。",
      },
    ],
    "timeline-domestic-family-photography": [
      {
        year: 1888,
        event:
          "George Eastman 推出 Kodak No. 1 相机，并以“You press the button, we do the rest.”为口号，推动摄影走向大众。中产家庭第一次能够轻松拍摄自己的快照；Kodak 相机与随后推出的廉价 Brownie（1900）促使家庭摄影迅速增长，大量关于日常生活、旅行与庆典的影像由此出现。",
      },
      {
        year: 1900,
        event:
          "Kodak Brownie 上市，标价 1 美元，面向儿童与大众。其简易性与低价格稳固了家庭相册的习惯。至1910年代，数以百万计的家庭快照记录着平凡时刻，“记录家庭之舒适”成为现代文化的一部分。",
      },
      {
        year: 1948,
        event:
          "Polaroid 于 1948 年底推出 Model 95 Land Camera，这是第一台实用的即时成像相机。家庭大约一分钟内即可获得成品照片，即时摄影也因此在家庭场景中迅速普及。由于不必将胶卷送往冲印店，人们还可以更私密地拍摄与保存亲密的日常时刻。",
      },
      {
        year: 1955,
        event:
          "纽约 MoMA 的里程碑展《The Family of Man》（1955）不仅“向全球言说”，也把家庭生活提升为堪称艺术摄影的题材。其500多幅影像中大量呈现父母与孩子的日常。这一展览帮助确认“家庭摄影”不仅是纪念，更具普遍意义与艺术价值。",
      },
      {
        year: 1975,
        event:
          "Nicholas Nixon 开始《The Brown Sisters》项目：自 1975 年起，他每年为妻子 Bebe 与她的三位姐妹拍摄一张合影，人物顺序始终固定。这个跨越数十年的系列成为关于衰老、亲情与变化的深刻记录，也代表了 20 世纪 70 年代对长期家庭纪实的转向——家庭相册本身由此成为艺术。",
      },
      {
        year: 1986,
        event:
          "Nan Goldin 发表《The Ballad of Sexual Dependency》（1986），以幻灯与摄影书的形式呈现其在70–80年代纽约的“家人”（即亲密朋友群）。这部由原始、亲密的快照构成的作品把“选择的家庭”与艰难真相（成瘾、暴力）带到公众眼前，重新界定了“家庭摄影”的边界。",
      },
      {
        year: 1991,
        event:
          "MoMA 举办《Pleasures and Terrors of Domestic Comfort》（1991），聚焦当代摄影中对家庭生活的探索（9–12月）。展出 70 位艺术家的 150 幅作品，包括 Tina Barney（中上层家庭）、Sally Mann（其子女肖像）、Larry Sultan（父母在家）。展览承认“家庭生活”的复杂性——温暖与焦虑并存——并成为将家庭摄影确立为严肃主题的转折点。",
      },
      {
        year: 1992,
        event:
          "两本风格迥异的家庭摄影书面世：Sally Mann《Immediate Family》（1992）在其乡间家中拍摄年幼子女的坦率肖像（常含裸体），以其美感获赞却因隐私与剥削问题引发争议；Larry Sultan《Pictures from Home》（1992）记录年迈父母的日常并结合其回忆，以诚实探寻“1950年代理想化家庭形象”与现实情绪之间的落差。二者体现了90年代对“家庭”深入与不回避的凝视。",
      },
      {
        year: 2010,
        event:
          "智能手机与社交媒体从根本上改变家庭摄影。2010年 Instagram 上线（及类似平台兴起）创造了全球分享空间，数十亿个日常家庭瞬间——幼儿迈步、餐桌与团聚——被分享至线上。“快照”美学与家庭相册转为数字形态。中期开始，“晒娃”（sharenting）进入公共讨论；影像的海量增长也带来隐私与记忆的新议题。",
      },
      {
        year: 2013,
        event:
          "“自拍时代”常被轻看，然其实是家庭摄影的延伸——人们记录自己及亲人。2013年“selfie”被牛津词典评为年度词。如今家庭影像常包含与亲人的合照（前置镜头），不论在家中还是旅行中。技术持续重塑家庭摄影：从正式肖像到随手快照再到今天的“手臂长度自拍”，其核心目标始终是捕捉并分享最重要的人与时刻。",
      },
    ],
    "timeline-feminist-photography": [
      {
        year: 1968,
        event:
          "在欧洲，VALIE EXPORT 的行为作品《Tap and Touch Cinema》（维也纳，1968）被摄影记录：她身着一个带帘幕、露出胸部的箱体，邀请陌生人伸手触碰。这件作品体现了 20 世纪 60 年代末女权主义与摄影之间具有挑衅性的交汇。与此同时，第二波女权运动激励女性艺术家将相机转向自己的身体与生活经验，直面社会禁忌；1968 至 1970 年间的妇女解放抗议也被广泛拍摄，使获得主体性的女性形象进入大众意识。",
      },
      {
        year: 1975,
        event:
          "1970年代中期，显性女权摄影蓬勃发展。开创性作品包括 Hannah Wilke《S.O.S. Starification Object Series》（1974–75）：她赤裸上身贴上口香糖“疤痕”，作为对物化的评论。同年，Eleanor Antin《Carving: A Traditional Sculpture》以37天的体重塑形摄影日记批评美丽标准。1975年，电影理论中“男性凝视”（Laura Mulvey）被提出，深刻影响女性摄影师如何策划其图像。合而观之，1975年常被视为“个人即政治”的分水岭。",
      },
      {
        year: 1977,
        event:
          "Cindy Sherman 的作品开始受到关注，并很快从女权主义视角得到解读。尽管她表示创作之初并无明确政治议程，《Untitled Film Stills》（1977–1980）仍迅速被视为对媒体女性刻板形象的尖锐批评。至 20 世纪 80 年代初，她与 The Pictures Generation 中的 Barbara Kruger、Sherrie Levine 等人，经常因颠覆女性被呈现的方式而进入女权艺术讨论。Sherman 的《Untitled Film Still #35》（1979）也成为有关女权主义、后现代主义与再现问题的讨论焦点。",
      },
      {
        year: 1985,
        event:
          "Guerrilla Girls 在纽约成立。这个由匿名女性艺术家组成的团体佩戴大猩猩面具，以幽默和图像海报揭露艺术界的性别与种族不平等。她们 1985 年的早期街头海报列出在主要博物馆获得个展的女性何其稀少；1989 年，她们又制作著名公交广告《Do Women Have to Be Naked to Get into the Met. Museum?》。这种将摄影图像与文字结合的行动成为女权艺术的标志。",
      },
      {
        year: 1990,
        event:
          "学术与展览在90年代回溯女性在摄影史中的贡献。例如大型巡回展与出版《Women Photographers: 1840–1990》；1996年，华盛顿的女性艺术博物馆举办《Silver Lens, Golden Leaf》。同时，90年代的艺术继续推进女权主题：Catherine Opie 记录 LGBTQ 与家庭社群（包括她在1994年以 BDSM 面罩与“Pervert”切字的自画像——对可见性与标签的大胆宣言）。伊朗裔艺术家 Shirin Neshat 以在女性面孔与手部覆以书法的系列作品探讨伊斯兰女性身份，并在1999年获威尼斯双年展大奖——显示女权主题的全球影响力。",
      },
      {
        year: 2010,
        event:
          "MoMA 举办《Pictures by Women: A History of Modern Photography》（2010–2011），集中呈现从 19 世纪至当代的女性摄影师。展览展出 200 余件作品，既强调长期被摄影史边缘化的女性贡献，也呈现女权主义推动的变化；Cindy Sherman、Claude Cahun、Tina Modotti 等人的许多作品都曾直接挑战性别规范。该展既是一场回顾，也是一条关于女性如何借摄影表达并争取主体性的历史线索。",
      },
      {
        year: 2013,
        event:
          "社交媒体与女权摄影出现显著交汇。年轻的女性主义者通过 Instagram 等平台分享挑战美貌标准的自画像（如 #nomakeupselfie），呼应70年代精神但影响范围扩大至数百万人。同年，#Girlgaze 项目走红，推广全球的少女摄影师。民主化意味着女权摄影不再局限于美术馆——它是一个话题标签、一个社群、一个人人可参与的运动。",
      },
      {
        year: 2016,
        event:
          "巡回展《Feminist Avant‑Garde of the 1970s》（Verbund 收藏）在国际巡展（含伦敦等地），展出48位艺术家、150件作品，系统呈现70年代的激进影像艺术（从 Birgit Jürgenssen 的身体改造到 Lynn Hershman Leeson 的虚构人格“Roberta”）。评论者指出，Hannah Wilke 或 Lynn Hershman 的自画像——曾被轻蔑——在社交媒体与自我形象争论的背景下被重新视作先见。展览巩固了70年代女权摄影的基础地位，并将之直接连到当下实践。",
      },
      {
        year: 2020,
        event:
          "女权摄影持续演变，并进一步关注种族、阶级与性别身份交织的议题。南非摄影师 Zanele Muholi 以强有力的自画像推进这一方向，其系列《Somnyama Ngonyama》重新想象黑人酷儿身份。#MeToo 运动自 2017 年起也形成鲜明的视觉表达，幸存者肖像与《Time》的《The Silence Breakers》封面成为标志性图像。女权摄影由此持续追问：谁能够被看见，又能以什么方式被看见。",
      },
    ],
    "timeline-classic-landscape": [
      {
        year: 1850,
        event:
          "19世纪中期——奠基与探索：风景摄影在1840–50年代与银版、卡罗法、湿版火棉胶等工艺同步出现。法国的 Gustave Le Gray 以合成负片平衡天空与海面推进海景技术；英国的 Roger Fenton 受浪漫主义影响记录田园景致。在美国，Carleton Watkins、Eadweard Muybridge、Timothy O’Sullivan 在测绘远征中拍摄优胜美地、内华达与西部宏伟景观。这些照片既是科学记录，也是扩张象征与艺术作品。",
      },
      {
        year: 1890,
        event:
          "19世纪末——绘画化的宏伟：至1880–90年代，大画幅与白金/蛋白印相带来卓越层次。风景影像常模仿哈德逊河派画作的构图，强调崇高的荒野。虽被后来者视为浪漫化，它确立了“经典风景”的视觉词汇：广阔视野、戏剧化光线与严谨构图。",
      },
      {
        year: 1932,
        event:
          "20世纪早期——现代主义的精确：1910–20年代，Edward Weston、Paul Strand 倡导“纯摄影”——锐利对焦与丰沛层次，反对软焦绘画主义。该精神在1932年结晶为 f/64 小组（Ansel Adams、Weston、Imogen Cunningham、Willard Van Dyke），成员以小光圈获取最大景深，追求技术完美。Adams 的“区域系统”成为曝光与放大的基石，使风景既科学精确又富情感。",
      },
      {
        year: 1940,
        event:
          "1930–40年代——保育与国家认同：Ansel Adams 与 Sierra Club 的合作把风景摄影与环保倡议相连。其图像在日历、图书与展览中的传播强化了“荒野是国家瑰宝”的观念；与此同时，Eliot Porter 对彩色摄影的探索表明，风景摄影可以超越黑白传统。",
      },
      {
        year: 1962,
        event:
          "1950–60年代——色彩与亲密：Eliot Porter 在 Sierra Club 画廊举办《In Wildness Is the Preservation of the World》（1962），标志着彩色摄影开始在艺术风景领域获得认可。他常聚焦森林与溪流的细部，由此拓展了风景摄影的尺度与观看方式。",
      },
      {
        year: 1980,
        event:
          "1970–90年代——变迁中的古典主义：即便“新地形”批判理想化风景，古典路径仍繁荣。David Muench、Christopher Burkett 创作光辉的大画幅彩色；英国的 Fay Godwin 将经典风景与对土地占有/通行权的社会评论结合。",
      },
      {
        year: 2005,
        event:
          "2000年至今——传统延续：Michael Kenna 的极简黑白风景与 Sebastião Salgado 的《Genesis》共同赞颂原始自然，延续了经典风景摄影的传统。数码大画幅系统如今已能达到可与胶片比拟的分辨率，但这一类型的核心——严谨构图、影调控制与对自然的敬意——并未改变。",
      },
    ],
    "timeline-new-topographics": [
      {
        year: 1963,
        event:
          "1960年代初，Ed Ruscha 自出版《Twentysix Gasoline Stations》（1963）与《Every Building on the Sunset Strip》（1966），以冷静目录化的方式记录路边场景，奠定一种“地形学”美学——拒绝浪漫化、趋向中性。其冷峻态度直接启发“新地形”摄影。",
      },
      {
        year: 1974,
        event:
          "Robert Adams《The New West》（1974）以苍白的住宅区、推土后的地块与新道路呈现郊区蔓延，堪为新地形的典范。其朴素、不粉饰的影像把分割扩张视为严肃的风景议题，成为里程碑式作品。",
      },
      {
        year: 1975,
        event:
          "George Eastman House 举办《New Topographics: Photographs of a Man-Altered Landscape》（1975），由 William Jenkins 策划。参展者包括 Robert Adams、Lewis Baltz、Joe Deal、Frank Gohlke、Nicholas Nixon、John Schott、Stephen Shore、Henry Wessel，以及 Bernd and Hilla Becher。展览以近乎临床式的中性风格拍摄工业地带、郊区与日常场所，用“被人类改造的风景”取代英雄化的荒野。尽管最初反响复杂，它后来仍被视为风景摄影史的重要转折点。",
      },
      {
        year: 1976,
        event:
          "1976 年，Bernd and Hilla Becher 在 Kunstakademie Düsseldorf 建立摄影教学项目，并持续任教至 1996 年。他们培养了 Thomas Struth、Candida Höfer、Thomas Ruff、Andreas Gursky 等摄影师，以客观、严谨的大画幅方法记录城市与工业景观。由此形成的 Düsseldorf School 将 New Topographics 的精神带入 20 世纪 80 至 90 年代，并推向全球。",
      },
      {
        year: 1990,
        event:
          "Bernd and Hilla Becher 获得 1990 年 Venice Biennale 雕塑类 Golden Lion，这是对摄影实践的突破性肯定，也表明他们的类型学影像已被视为观念艺术。New Topographics 的影响由此进入艺术体制核心。",
      },
      {
        year: 2009,
        event:
          "《New Topographics（Revisited）》大型回顾巡展在罗切斯特开幕（2009），再度呈现原10位艺术家的百余幅作品，并出版厚重图录。策展方指出原展已成“史上第二被引用最多的摄影展”，术语“New Topographics”进入词汇，广泛指涉未出生于1975年的后继者。",
      },
      {
        year: 2010,
        event:
          "SFMOMA 于 2010 年 7 至 10 月展出巡回版《New Topographics》，并将 1975 年的原展称为风景再现发生激进转向的信号。媒体回顾认为它改写了风景摄影的规则，在停车场与住宅区中发现平凡之美。最初被视为“反 Ansel Adams”的冷静风景，到 2010 年代已经进入艺术摄影主流。",
      },
      {
        year: 2011,
        event:
          "2011–12年，巡展以科隆、鹿特丹、毕尔巴鄂等地收官。全球兴趣显示“当代人类景观”的普遍意义。学派影响遍及杜塞尔多夫大型彩色摄影到网络社群（Flickr“New Topographics”小组），从小众风格成长为广泛的审美与批评框架。",
      },
    ],
    "timeline-urban-landscape": [
      {
        year: 1935,
        event:
          "Berenice Abbott 承接 WPA Federal Art Project 项目《Changing New York》，以大画幅摄影清晰记录 Manhattan 的迅速变化；1939 年出版的同名摄影书确立了城市风景兼具档案与艺术的地位。同一时期，Walker Evans 的《American Photographs》（1938）也提升了日常城市景观在摄影中的价值。",
      },
      {
        year: 1967,
        event:
          "MoMA 的《New Documents》展出 Garry Winogrand、Lee Friedlander 与 Diane Arbus 的作品，并引入“社会风景”的观看方式。三位摄影师以 35mm 相机拍摄自发而主观的街头场景，使对现代城市的个人化诠释获得艺术机构认可。",
      },
      {
        year: 1975,
        event:
          "George Eastman House 的《New Topographics》把视野扩展至城市边缘与郊区，包括住宅区、停车场和工业园。Robert Adams、Lewis Baltz、Stephen Shore 等人以中性、克制的风格，重新定义城市风景，使郊区蔓延与日常建筑也成为值得严肃观看的对象。",
      },
      {
        year: 1976,
        event:
          "MoMA《William Eggleston’s Guide》为彩色在艺术摄影正名；其高饱和染料转印把平凡城市—郊区景象的“静谧诗意”带入主流。",
      },
      {
        year: 1990,
        event:
          "杜塞尔多夫学派将城市风景拉至纪念碑尺度：Andreas Gursky、Thomas Struth、Candida Höfer、Thomas Ruff 制作宏大而细密的城市图像；Gursky《Paris, Montparnasse》（1993）象征全球化视角与类型学的严谨。",
      },
      {
        year: 2011,
        event:
          "Google 街景被作为“现成影像”进入讨论：Michael Wolf 以街景截屏获世界新闻摄影奖关注；Jon Rafman 自2008年起的《9‑Eyes》策展自动化的城市档案，引发作者与监控的议题。",
      },
    ],
    "timeline-fashion-photography": [
      {
        year: 1911,
        event:
          "Edward Steichen 接下挑战：1911 年他为 Paul Poiret 的高级定制拍摄模特，并将作品发表于《Art et Décoration》（1911 年 4 月）。这些柔焦的自色版（autochrome）照片——以优雅的姿态与精心的布光披展模特——被广泛认为是第一批“现代时尚摄影”。Steichen 的图像不再只是“说明服装”，而是传达衣料的质感、动势与魅力。1911 年这次拍摄与此前依赖插图或呆板影棚肖像的旧法分道扬镳，标志摄影在时尚界“占有一席之地”的时刻。当代作者 Jesse Alexander 写道，Steichen 1911 年的影像是第一批以一种“既能传达服装物理质感又能呈现其形式外观”的方式去描绘服装的照片。时尚摄影作为一个类型由此诞生——它把艺术与商业融合，呈现设计的“气质/氛围”，不仅仅是外观。",
      },
      {
        year: 1913,
        event:
          "洞见摄影潜力的 Condé Nast 于 1913 年聘请 Baron Adolph de Meyer 成为《Vogue》的第一位全职时尚摄影师。正值 Condé Nast 收购《Vogue》并将其打造为一线时尚杂志之际，de Meyer 的入职令该领域迈向职业化。他以闪耀的、近似绘画主义的风格著称，常以逆光为上流名媛与女演员拍摄巴黎新款礼服，使人物被柔光光环笼罩。de Meyer 在 1910 年代为《Vogue》与《Vanity Fair》拍摄的作品奠定了经典时尚摄影的诸多惯例：强调优雅与氛围、影像中的“细腻氛围感”，以及“模特作为奢华幻想的化身”。随着 de Meyer 的成功，至 1920 年代中期，时尚杂志基本以模特照片取代手绘插图——公众获取时尚的方式由此发生断裂式转变。",
      },
      {
        year: 1920,
        event:
          "Edward Steichen 在一战后回归摄影，并于 1923 年出任《Vogue》与《Vanity Fair》的首席摄影师，开启 Condé Nast 时尚摄影的黄金时代。他以戏剧化、锐利布光的影棚肖像塑造 Roaring Twenties 的社会名流与 Hollywood 明星形象，也将立体主义背景和大胆机位带入时尚大片。20 年代后期，George Hoyningen-Huene 与 Horst P. Horst 加入《Vogue》；Horst P. Horst 的《Mainbocher Corset》（1939）后来成为经典。Cecil Beaton 将这一类型的目标概括为捕捉颓靡之美并“售卖一种梦”。1932 年，Steichen 的时尚摄影在 Museum of the City of New York 展出，成为博物馆较早对这一类型的认可。与此同时，Steichen 与 Paul Outerbridge 对三色碳转印工艺的实验，也预示了彩色时尚摄影的未来。",
      },
      {
        year: 1934,
        event:
          "竞争对手《Harper’s Bazaar》在 1934 年迎来艺术总监 Alexey Brodovitch，他立刻以激进的编排与大胆字体使杂志面貌焕然一新，并委约年轻摄影师创作充满电影感与动势的作品。1935 年，他邀请 Martin Munkacsi 拍摄泳装专题——发表了一张前所未有的照片：一位模特在海滩奔跑、发丝飞扬、充满运动感。该照片（1935）被视为时尚影像首次呈现如此自发与动作性，促成主编 Carmel Snow 的名言“让它动起来！”也宣告了假人般静态姿势的终结。Brodovitch 亦培养了 Louise Dahl‑Wolfe，她自 1936 年起常在自然光下进行户外时尚拍摄。",
      },
      {
        year: 1947,
        event:
          "Christian Dior 在巴黎发布“New Look”，时尚摄影以相应的雄心应答。马格南摄影师 Henri Cartier‑Bresson 走上巴黎街头，抓拍身着 Dior 蜂腰大摆裙的女性，将纪实与时尚结合。与此同时，伦敦的 Cecil Beaton 为《Vogue》导演华丽场景照，重燃战前的旧式魅力。其 1948 年的一幅名作拍到一位身着 Dior 礼服的模特在宏伟楼梯上款款而下，周围人群仰望——戏剧性与对战前优雅的怀旧呼之欲出。战后年代在改良胶片技术与丰厚杂志预算的助推下，时尚摄影的“黄金时代”真正开启。Horst P. Horst 继续创作结构严谨、常引古典雕塑之姿的影棚照片；Erwin Blumenfeld 则以创新的彩色封面著称（例如 1950 年《Vogue》封面把模特面孔抽象为仅有双唇、单眼与美人痣）。时尚图像成为流行文化核心；模特与摄影师本身亦成明星。",
      },
      {
        year: 1955,
        event:
          "Richard Avedon 以《Dovima with Elephants》（1955 年 8 月）革新时尚摄影。作品为《Harper’s Bazaar》在巴黎 Cirque d’Hiver 实地拍摄：模特 Dovima 身着黑色 Dior 礼服，优雅地站在两头大象之间。Avedon 同时捕捉动作与叙事，让画面仿佛电影中的凝固瞬间，并将美与兽、自由与束缚并置；它后来被广泛视为最伟大的时尚照片之一。整个 20 世纪 50 年代，Avedon 持续离开影棚，鼓励模特在街头与外景中跳跃、起舞和回应环境，使运动与自发性成为时尚影像的新标志。Norman Parkinson、Henry Clarke 也在真实城市空间中拍摄时装；此后 David Bailey、Terence Donovan 与 Brian Duffy 等英国摄影师进一步把青春活力带入 London 街头。",
      },
      {
        year: 1966,
        event:
          "Helmut Newton 自 1966 年起与《Vogue》长期合作，开启前卫、情色时髦的时代。在巴黎 1966 年，他于夜色中的 Rue Aubriot 拍下 Yves Saint Laurent 女式无尾晚礼服“Le Smoking”。这张常被称为“Le Smoking”的照片（发表于 1966 年法国版《Vogue》）呈现一位中性气质的女性模特，穿利落的燕尾服、在昏暗巷道中叼烟自信而立。黑色电影式的氛围与性别越界的造型具有革命性。Newton 于 60 年代末至 70 年代的作品（例如 1975 年的仅穿高跟鞋与马鞍的女性，或手持皮鞭的高级时装“女战士”）把公开的性恋物引入高级时尚摄影——此前未有。他的作品在光鲜控制之下又带着“危险/窥视”，把诱惑与威胁注入原本端庄的时尚图像世界。与 Newton 并行，Guy Bourdin 在 1970 年代的《French Vogue》里推动色彩超现实、有时带有阴郁叙事的影像：模特如同遇害的摆放、怪诞构图使产品（鞋、口红）退居其次。Bourdin 与 Newton 的“性魅力”饱和图像定义了 1970 年代挑衅性的时尚社论视觉，证明时尚摄影可以在震撼与诱惑之间取得平衡，此趋势在后世更加强烈地回潮。",
      },
      {
        year: 1988,
        event:
          "《Vogue》（美版）主编 Anna Wintour 上任（1988）即改写封面法则：她的首张封面（1988 年 11 月，由 Peter Lindbergh 拍摄）不是影棚特写，而是一张 19 岁的 Michaela Bercu 身穿高级定制 Christian Lacroix 外套、却搭配石洗牛仔裤，在街头大笑的照片。",
      },
      {
        year: 2000,
        event:
          "伴随名人文化与高度性化的广告，时尚摄影在 2000 年代走向新的极端。Terry Richardson 以快照式、性张力强烈的 American Apparel 广告与社论作品，成为“porno chic”回潮的代表。Tom Ford 主导的 Gucci 于 2003 至 2004 年推出由 Mario Testino 拍摄的争议广告，以越界画面制造话题；Steven Meisel 为《Vogue Italia》创作的《State of Emergency》（2006）则把模特塑造成遭警方粗暴对待的恐怖嫌疑人，通过时尚影像评论 9/11 之后的政治氛围。与此同时，《Vogue》等杂志大量采用 Annie Leibovitz 等摄影师拍摄的名人封面，重心由模特转向名人时尚偶像。Photoshop 修图日益常态化，也引发身体形象与真实性争论；作为回应，Dove 的《Campaign for Real Beauty》以 Rankin 拍摄、较少修饰的普通女性肖像强调真实与多元。2000 年代的时尚摄影因此在高度打磨的幻想与粗粝的真实感之间摆动。",
      },
      {
        year: 2010,
        event:
          "智能手机与 Instagram 带来的影像民主化深刻改变了时尚摄影，传统上由少数明星摄影师主导大型项目的行业结构开始受到社交媒体新锐的挑战。2014 年，《Vogue》首次将 Instagram 图像用于封面增刊，以 Cara Delevingne 的自拍呈现社交媒体与杂志的新协同。2018 年，美国版《Vogue》首次启用黑人摄影师拍摄封面：23 岁的 Tyler Mitchell 为 Beyoncé 创作了 9 月号封面，并以自然光与带有微妙奇幻感的造型广受赞誉。更多女性、BIPOC 与 LGBTQ+ 摄影师也开始获得重要委约。与此同时，《Vogue Italia》刊出以 iPhone 拍摄的完整社论，Jean-Paul Goude 为《Paper》创作的 Kim Kardashian 封面《Break the Internet》（2014）在网络上广泛传播，CGI 模特 Lil Miquela 则提出真实与模拟的新问题。到 2010 年代末，时尚摄影的主要观看场景已从纸媒延伸至手机屏幕，但其目标仍与 Edward Steichen 在 1911 年确立的方向相连：以有吸引力的图像捕捉服装精神与时代气息。",
      },
    ],
    "timeline-product-photography": [
      {
        year: 1856,
        event:
          "“产品摄影”这一说法尚未出现时，其雏形已经形成。法国摄影师 Adolphe Braun 出版一部收录 288 张照片的影集，呈现 Countess Virginia Oldoini（the Countess di Castiglione）身着各类华服的形象。虽然本质上是一项个人形象工程，这部影集也近似服饰目录，可被视为以摄影展示和推广产品的早期例子之一。彼时广告多依赖木刻版画，而 Braun 的作品证明了摄影在呈现珠宝、丝绸等物件材质与细节方面的独特优势，也为照片进入商品目录与商业宣传奠定基础。",
      },
      {
        year: 1880,
        event:
          "1880 年 3 月 4 日，《New York Daily Graphic》首次在报纸上以网点法（halftone）印刷照片。这项把照片转换为可印刷网点的技术，使产品照片首次能够与文字一同大规模传播。至 1880 年代末，零售商与制造商开始在广告和目录中采用网点图像；Sears Roebuck 的目录也于 1890 年代加入缝纫机、手表等商品照片，比木刻插图更加清晰可信。Kodak 的早期广告同样使用相机拍摄的网点照片，以产品摄影来销售摄影产品本身。",
      },
      {
        year: 1930,
        event:
          "蓬勃发展的广告业开始把产品摄影视为专门门类。随着光面杂志兴起，品牌开始寻找能够让物品更具吸引力的摄影师。Paul Outerbridge 是其中的先驱，他以构图精密、色彩浓郁的静物著称，并在《Photographing in Color》（1937）中展示以三色碳转印工艺拍摄的蛋、杯子与化妆品等日常物件。Margaret Bourke-White 也为《Fortune》等刊物创作强调机器质感的产品影像；László Moholy-Nagy 则以大胆角度与蒙太奇进行实验。至 20 世纪 30 年代末，干净布光、清晰细节与现代主义构图已成为产品摄影的重要美学。Kodak 在 1934 年的广告标语“You see it in a photograph—you want it.”准确概括了这种影像的商业力量。",
      },
      {
        year: 1950,
        event:
          "二战后的消费繁荣使产品摄影需求激增，杂志与报纸充斥经过精心布置的汽车、厨房电器与包装食品影像。电子闪光和 35mm 单反相机，例如 1959 年的 Nikon F，使摄影师更容易获得清晰细节与均衡光线。此期的标志性影像之一是《Marlboro Man》（1955），最初由 Leonard McCombe 为《Life》拍摄，后来被改造为广告；它以生活方式代替产品本身成为画面核心。影棚则广泛使用灯箱、偏振镜与移轴镜，控制背景、反光和透视。至 20 世纪 50 年代末，J. Walter Thompson 等广告公司已建立专门摄影部门，电视广告也借用静物产品摄影的精密布光和最佳视角，进一步推动商业影像专业化。",
      },
      {
        year: 1990,
        event:
          "数字革命深刻改变了产品摄影。20 世纪 90 年代初，高端数字扫描背与首批数码单反相机，例如 Kodak DCS（1991），使影棚能够直接采集达到印刷分辨率的电子图像。至 90 年代末，大量目录影棚全面转向数码，不再需要等待胶片冲洗或 Polaroid 试拍，工作流程更快、成本更低。互联网与电商的兴起则带来图像数量的爆炸：Amazon 与 eBay 均于 1995 年上线，每件线上商品开始需要直观、清晰的照片。Sears、IKEA 等目录零售商也利用数字技术快速拍摄、修饰并跨平台复用大量产品图；Victoria’s Secret 于 1998 年将纸质目录影像用于在线商店，成为早期跨媒介案例。消费者也日益要求产品照片真实、准确地呈现实物。",
      },
      {
        year: 2000,
        event:
          "数字产品摄影无处不在并进一步细分。360° 产品摄影兴起——在转盘上拍摄一系列静帧并拼为交互动画，让用户可‘旋转’观看；中 2000 年代在电子产品与鞋类网站上流行。Photoshop 与专门工具实现合成与‘隐形模特’（ghost‑mannequin）效果（例如衣物被撑起但不见模特）。零售巨头如 Amazon 设定标准（约 2004 年）：纯白背景（RGB 255,255,255）与最小像素边长——几乎把全球电商的产品摄影风格塑造成统一、实用。同期，图库网站（Getty、Shutterstock）高速增长，为预算有限的广告主与网站提供通用产品素材。智能手机也开始发挥作用：尽管 2007 年首代 iPhone 仅 200 万像素，难堪大任，但至 2000 年代末，小型商家已用准专业相机或手机为 Etsy（2005 年创立）等平台自行拍摄，人人可用灯箱与数码相机在家制作尚可的产品图——与旧日大影棚时代大相径庭。",
      },
      {
        year: 2010,
        event:
          "这一时期两条趋势并行。其一，极简、生活方式化的产品影像崛起。Apple 以超干净、高分辨的产品图树立标杆（如 2010 年 iPhone 4 的官方图片清晰到近乎超现实），同时许多品牌在‘标准白底’之外增补‘社论式’产品图：展示使用场景或理想化场面（如 Kinfolk 风格的案头编排）。Instagram（2010）推动‘平铺（flat lay）’成为营销常态。其二，技术飞跃提升精度与自动化：5000 万像素以上的 DSLR 与 Phase One 背提供极致细节（奢侈品尤需，如 2015 年 LV 的皮纹特写可在线近距离放大）；机器人机位与软件令流水线拍摄成为可能，保证大量 SKU 的一致光位与角度——对大型电商至关重要。消费者端，12MP+ 的智能手机与 App 让小体量商家可用手机完成电商图；教程普及，教 Etsy 卖家用 iPhone 与小灯箱拍好图。至 2010 年代末，产品摄影甚至进入 AR：以照片建模生成 3D，让用户把虚拟产品‘放进’房间（如 2017 年 IKEA App）。贯穿始终的口号仍是‘一致与准确’——Amazon 等坚持标准化，同时允许每个商品 9+ 张图，鼓励多角度与场景图。由此，产品摄影在高端广告中是一门‘诱惑之艺’，在电商执行中又是一门‘转化之工’。",
      },
      {
        year: 2020,
        event:
          "迈向新的前沿：AI 与 CGI 日益逼真，部分目录与汽车画册已以三维渲染取代传统拍摄。然而 150 余年来确立的原则依旧有效：好光线、清晰呈现，再加一点诱惑。无论 1850 年的紧身胸衣银版，还是 2025 年的运动鞋 CGI 合成，目标始终如一——让观者‘想伸手把它拿起’。产品摄影仍是商品与图像、商业与欲望之间不可或缺的纽带。",
      },
    ],
    "timeline-advertising-photography": [
      {
        year: 1880,
        event:
          "《New York Daily Graphic》于 1880 年 3 月 4 日首次在报纸上刊登网点照片，标志摄影进入广告传播。至 1880 年代末，百货公司开始刊登店面与产品的网点照片，为消费者提供比雕版插图更真实的观看方式。到 1890 年代，网点技术已能支持自行车、钢琴与时装等商品的精细广告，摄影由此成为具有可信度的销售工具。",
      },
      {
        year: 1920,
        event:
          "平面广告的黄金时代全面拥抱摄影。Coca-Cola 以清爽利落的产品照片逐渐取代插画，例如 1929 年将饮料倒在冰块上的画面；Ford 则以戏剧化角度呈现 Model A。Chanel 等品牌聘请 Edward Steichen、George Hoyningen-Huene 拍摄华丽广告，将杂志社论风格与品牌推广结合。广告公司开始建立自有影棚，Nickolas Muray 等摄影师也率先尝试早期彩色广告。",
      },
      {
        year: 1954,
        event:
          "Marlboro 推出《Marlboro Man》广告。Leonard McCombe 与后来的摄影师将真实牛仔置于广袤西部景观中，把 Marlboro 从面向女性的温和品牌重塑为阳刚象征，也证明一张照片可以像产品本身一样有力地销售形象与生活方式。",
      },
      {
        year: 1971,
        event:
          "Keep America Beautiful 的反污染公益广告《Crying Indian》（1971）中，演员 Iron Eyes Cody 面对污染景象落下一滴泪。虽然原作是电视广告，其关键画面仍在印刷媒体中广泛传播并影响公众舆论。20 世纪 70 年代，越来越多具有社会关怀的广告借用纪实影像介入公共议题，从反猎捕海豹到反吸烟运动皆是如此。",
      },
      {
        year: 1980,
        event:
          "Absolut 伏特加于 1981 年启动‘无限变化的酒瓶’创意广告，摄影师以无尽概念将酒瓶融入画面；Calvin Klein 的 1981 年内衣广告由 Bruce Weber 拍摄，把社论式魅力引入商业；Benetton 在 Oliviero Toscani 主导下以挑衅性的纪实照片（多元人像、社会议题）打造“争议即品牌”的全球话语。",
      },
      {
        year: 1990,
        event:
          "Adobe Photoshop 1.0 革新了影像处理，使无缝合成与精细修图成为可能。Nike 的《Jumpman》影像由 Jacobus Rentmeester 拍摄，并逐渐成为全球性符号；Richard Avedon 为 Versace 创作的名人肖像广告则把商业画面塑造成电影化场景。数字技术由此重塑广告摄影，同时知名摄影师与名人代言继续主导视觉传播。",
      },
      {
        year: 2006,
        event:
          "Dove 的《Campaign for Real Beauty》由 Rankin 拍摄，以较少修饰、涵盖不同年龄与身材的女性肖像挑战单一审美标准，并引发广泛讨论。同期，American Apparel 以直接闪光、带有强烈性暗示的低保真影像塑造叛逆的品牌审美。",
      },
      {
        year: 2015,
        event:
          "社交媒体推动广告摄影转向移动优先。Apple 的《Shot on iPhone》将用户照片带上大型广告牌；Nike 的《Dream Crazy》（2018）以 Platon 拍摄的 Colin Kaepernick 极简肖像展现直接视觉语言的力量。Fenty Beauty 因包容性的品牌影像受到赞誉，而源于 Instagram 的“网红风格”也逐渐成为生活方式广告的主流。",
      },
      {
        year: 2018,
        event:
          "CGI 与人工智能开始进入商业摄影。由 Cameron-James Wilson 创造的虚拟模特 Shudu 于 2018 年进入品牌合作，即使没有真实相机拍摄，也能参与完整的商业传播。这类全合成人物与场景预示广告影像正在从数字修图进一步迈向完全生成的品牌视觉。",
      },
    ],
    "timeline-film-based-experiments": [
      {
        year: 1922,
        event:
          "Man Ray 推出 Rayograph：将物体直接置于感光纸上曝光，无需相机即可形成影像；他后来又结合 solarization，在显影过程中局部反转影调，制造幽灵般的效果。同一时期，Christian Schad 与 László Moholy-Nagy 等达达主义艺术家也通过 photogram 和抽象旋涡式构图，持续拓展摄影实验的边界。",
      },
      {
        year: 1930,
        event:
          "超现实摄影探索多重曝光与组合印相：Maurice Tabard 把女性面孔叠置于云层；Brassaï 试验对裸体的扭曲反射；早期红外片（如 Kodak Aerochrome）为风景带来异世的假彩色效果。",
      },
      {
        year: 1955,
        event:
          "艺术家开始把 Polaroid 胶片作为即时实验工具。Ansel Adams 用它评估影调范围，Lucas Samaras 则在 SX-70 相纸显影过程中直接操控乳剂，形成近似绘画的抽象效果。Kodak HIE 等红外胶片也在 20 世纪 60 年代因其带有辉光的超现实影像而流行。",
      },
      {
        year: 1975,
        event:
          "玩具相机运动兴起。Nancy Rexroth 使用 Diana 相机创作《Iowa》（1977），主动把虚焦与漏光转化为审美语言。大型 20×24 Polaroid、乳剂转移、针孔摄影与 chemigram 等技法也成为艺术实验手段，为强调精确控制的摄影提供了模拟替代路径。",
      },
      {
        year: 1995,
        event:
          "在数字摄影逐渐普及之际，模拟实验达到新的高峰。Lomography（1992 年创立）倡导使用 Holga 与 LOMO LC-A 进行随性、偶然的拍摄；交叉冲洗、film soup 与撕剥 Polaroid 等技法也重新流行，并受到独立摄影社群与小型画廊的推崇。",
      },
      {
        year: 2015,
        event:
          "胶片实验演化为对模拟的数字致敬：湿版火棉胶与铁版复兴、以咖啡为显影剂的 Caffenol 带回独特质感；同时，这些模拟效果（漏光、颗粒）普遍作为 App 滤镜被调用，说明模拟美学已成为数字视觉语言的一部分。",
      },
    ],
    "timeline-photomontage": [
      {
        year: 1855,
        event:
          "Oscar Rejlander 的《The Two Ways of Life》（1857）与 Henry Peach Robinson 的《Fading Away》（1858）采用 combination printing，将多张底片分层组合为复杂画面。这些精心摆拍与合成的叙事影像挑战了绘画在宏大视觉叙事中的主导地位，也成为照片拼贴与合成摄影的重要先例。",
      },
      {
        year: 1917,
        event:
          "John Heartfield、George Grosz 等 Berlin 达达主义者发展出 photomontage，以剪切、重组的报纸照片讽刺政治生活。这种以现成图像进行批判性再组合的方法，在一战后的 Europe 成为颠覆性艺术的重要语言。",
      },
      {
        year: 1925,
        event:
          "John Heartfield 在《Adolf the Superman》（1932）等反纳粹作品中，将照片拼贴转化为尖锐的政治批评。与此同时，Alexander Rodchenko 与 Varvara Stepanova 等构成主义艺术家把蒙太奇用于宣传设计和杂志版面；《Vogue》与《Vanity Fair》等西方刊物也开始采用蒙太奇，塑造现代编辑视觉。",
      },
      {
        year: 1950,
        event:
          "超现实主义照片拼贴在全球扩展。Grete Stern 在 Argentina 创作梦境主题的拼贴画面，Milan Grygar 以带有政治色彩的 Prague Spring 海报介入现实，The Beatles 的《Sgt. Pepper》专辑封面也把大量剪切图像组织成标志性的群像。拼贴由此成为构建视觉叙事的广泛方法。",
      },
      {
        year: 1975,
        event:
          "Jerry Uelsmann 通过多台放大机和复杂暗房技术推进照片合成，创作无接缝的超现实银盐印相。新闻领域中，《National Geographic》等刊物对照片的改动引发影像伦理争论；朋克杂志与平面设计则拥抱粗糙、低成本的 DIY 拼贴美学。",
      },
      {
        year: 1995,
        event:
          "Photoshop 使照片拼贴走向大众，数字合成逐渐取代剪刀与胶水，让多层影像可以无缝结合，并制造具有照片真实感的虚构画面。Peter Kennard 等艺术家把 John Heartfield 的政治讽刺传统转入数字媒介，电影海报与政治艺术也因新的图像处理工具而获得更大的操控空间。",
      },
      {
        year: 2015,
        event:
          "照片拼贴的美学已融入网络迷因、社交媒体视觉与政治艺术。Ai Weiwei 等人通过 Instagram 上的拼贴实践重新激活其行动主义潜力；与此同时，deepfake 等技术进一步模糊合成图像与照片现实之间的边界，并对影像真实性提出新的伦理问题。",
      },
    ],
    "timeline-instant-polaroid": [
      {
        year: 1947,
        event:
          "Edwin Land 在发布会上推出 Polaroid Land Camera Model 95，约一分钟即可得到黑白成片。即时摄影由此诞生，并改变了随意拍摄的文化。",
      },
      {
        year: 1963,
        event:
          "Polaroid 发布首款即时彩色胶片 Polacolor，为业余与专业摄影拓展了新的创作可能。时尚与广告摄影师也开始在片场使用 Polaroid 进行快速试拍与确认。",
      },
      {
        year: 1972,
        event:
          "折叠式单反即时相机 SX‑70 面世（集成式胶片包）。其流线设计与无需撕剥化学纸的流程令其成为文化偶像，被 Andy Warhol、Walker Evans、David Hockney 等用于实验创作。",
      },
      {
        year: 1977,
        event:
          "Polaroid 成为创意产业的常备工具。Lucas Samaras 等艺术家用它创作观念性自画像；Helmut Newton 则把即时照片用于试拍，其中部分影像后来也作为独立艺术作品展出。",
      },
      {
        year: 1986,
        event:
          "Polaroid 推出 35mm 即时胶片盒与 Spectra 系统，进一步扩展即时成像的格式。即时胶片也被广泛用于护照照片、证件制作与执法记录。",
      },
      {
        year: 1991,
        event:
          "随着一小时冲印店与早期数码相机提供更快的影像周转，即时摄影开始式微。Polaroid 的营销重点逐渐转向怀旧价值与细分的专业用途。",
      },
      {
        year: 2001,
        event:
          "Polaroid Corporation 申请破产，即时胶片生产大幅放缓。艺术家与小众摄影社群开始囤积剩余胶片。",
      },
      {
        year: 2008,
        event:
          "The Impossible Project 成立，并利用旧设备重启 Polaroid 胶片生产。这项自下而上的行动将即时摄影重新带给新一代爱好者。",
      },
      {
        year: 2017,
        event:
          "Polaroid Originals 推出（2020 年更名回 Polaroid），以更新的化学配方复兴相机与胶片；与设计师和艺术家的合作让即时摄影重获文化号召力。",
      },
      {
        year: 2020,
        event:
          "在模拟复兴与社媒审美推动下，即时摄影再度兴盛。限量配方、与时尚品牌联名，以及即时/数字混合相机共同让这一介质保持活力。",
      },
    ],
    "timeline-ai-cgi-photography": [
      {
        year: 1965,
        event:
          "Bell Labs 与 University of Utah 等机构开展早期计算机图形研究，生成了当时仍较为粗糙的合成图像，并为 CGI 日后进入视觉媒体奠定基础。",
      },
      {
        year: 1982,
        event:
          "电影《Tron》大规模采用 CGI 环境，激发了商业领域对计算机生成影像的兴趣。它虽然不是传统意义上的摄影，却标志着计算技术与视觉叙事开始深度结合。",
      },
      {
        year: 1995,
        event:
          "CGI 在广告与产品可视化中普及。汽车广告开始完全以 3D 渲染展示车型，实现完美光线与以往实拍不可能的机位。",
      },
      {
        year: 2005,
        event:
          "V‑Ray、KeyShot、Autodesk 3ds Max 等写实渲染软件使电子、汽车与奢侈品目录中的超写实 CGI 产品图成为常规；早期 AI 工具开始用于材质生成。",
      },
      {
        year: 2010,
        event:
          "GPU 计算与实时渲染进步令 CGI 更快更省。时尚品牌开始在样本册中试验全 CGI 场景，将真实与数字元素混合。",
      },
      {
        year: 2017,
        event:
          "对抗生成网络（GAN）使 AI 能生成逼真的人脸与物体。图库开始试水 AI 生成影像，以规避模特授权限制。",
      },
      {
        year: 2018,
        event:
          "由 Cameron-James Wilson 创造的 Shudu Gram 成为最早受到广泛关注的虚拟网红之一。她在现实中并不存在，却能够展示奢侈品并参与品牌传播，呈现了 AI 与 CGI 虚拟模特的商业路径。",
      },
      {
        year: 2020,
        event:
          "DALL·E 等生成式 AI 模型开始根据文本提示生成具有照片感的合成图像。广告领域的早期采用者将 AI 用于 moodboard、概念图，甚至最终的广告视觉。",
      },
      {
        year: 2022,
        event:
          "Midjourney 与 Stable Diffusion 引爆 AI 影像创作。品牌试验完全由 AI 生成的广告活动，同时关于真实性与版权的争论加剧。",
      },
      {
        year: 2024,
        event:
          "混合流程把真实摄影与 AI 后期结合以求成本与效率；部分电商平台开始规模化部署完全 AI 生成的产品照片，显示商业影像可能从‘相机拍摄’进一步转向‘模型生成’。",
      },
    ],
  };

  return (
    <div
      className={`subgenre-detail-page ${
        language === "CN" ? "subgenre-cn" : ""
      }`}
    >
      {/* Header */}
      <div className="subgenre-header">
        <Link to="/genres" className="back-link">
          {language === "CN" ? "← 返回摄影风格" : "← Back to Genres"}
        </Link>
        {/* <div className="breadcrumb">
          <span>{parentGenre.title}</span> /{" "}
          <span>{currentSubgenre.title}</span>
        </div> */}
        <h1>
          {language === "CN"
            ? genresZh.subgenres[currentSubgenre.id] || currentSubgenre.title
            : currentSubgenre.title}
        </h1>
      </div>

      {/* Introduction Section */}
      <section className="introduction-section">
        <h2>{language === "CN" ? "简介" : "Introduction"}</h2>
        <p className="introduction-text">
          {subgenreIntroductions[currentSubgenre.id] ||
            currentSubgenre.description}
        </p>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <h2>{language === "CN" ? "历史时间线" : "Historical Timeline"}</h2>
        <div className="timeline-container">
          {timelineEvents.length > 0 ? (
            <div className="timeline">
              {(language === "CN" && timelineZh[currentSubgenre.timelineId]
                ? timelineZh[currentSubgenre.timelineId]
                : timelineEvents
              ).map((event, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-year">{event.year}</div>
                  <div className="timeline-content">
                    <div className="timeline-event">
                      {renderTimelineEventWithPhotographerLinks({
                        text: event.event,
                        photographerDirectory,
                        enabled: TIMELINE_LINKED_SUBGENRES.has(subgenreId),
                        language,
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-timeline">
              <p>
                {language === "CN"
                  ? "该风格的时间线正在整理中，敬请期待！"
                  : "Timeline data for this subgenre is being compiled. Check back soon!"}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Photographers Section */}
      <section className="photographers-section">
        <h2>{language === "CN" ? "关键摄影师" : "Key Photographers"}</h2>
        {loading ? (
          <div className="loading-container">
            <p>{language === "CN" ? "正在加载摄影师..." : "Loading photographers..."}</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
          </div>
        ) : photographers.length > 0 ? (
          <div className="moma-photographers-grid">
            {photographers.map((photographer, index) => (
              <Link
                to={`/photographers/${photographer.id}`}
                className="moma-photographer-link"
                key={photographer.id}
              >
                <div className="moma-photographer-item">
                  <h3 className="photographer-name">{photographer.name}</h3>
                  <div className="photographer-details">
                    <p className="photographer-nationality">
                      {photographer.nationality}
                    </p>
                    {photographer.years && (
                      <p className="photographer-years">{photographer.years}</p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="no-results">
            <p>
              {language === "CN"
                ? "该风格暂未匹配到摄影师，请稍后再看。"
                : "No photographers found for this subgenre yet. Check back soon!"}
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default SubgenreDetailPage;
